/**
 * Native-shell refresh-token storage.
 *
 * Web clients receive the refresh token as an HttpOnly cookie on
 * `.packetprospector.com` and the browser sends it back automatically on the
 * cross-subdomain POST to `/api/auth/refresh`. Native Capacitor shells (the
 * Dispatch iOS / Android app) can't piggy-back on that flow — the WebView
 * runs at `capacitor://localhost` (iOS) / `https://localhost` (Android), so
 * the cookie's domain doesn't match the WebView's origin AND `SameSite=Lax`
 * blocks cross-site POSTs from including it.
 *
 * For native, the backend additionally returns the refresh token in the login
 * response body. We persist it here via `@capgo/capacitor-native-biometric`'s
 * `setCredentials`/`getCredentials`/`deleteCredentials`, which are genuinely
 * Keychain-backed on iOS (`SecItemAdd` with `kSecClassGenericPassword`) and,
 * on Android, AES-GCM encrypted with a hardware-backed AndroidKeyStore key
 * before the ciphertext ever touches disk — unlike `@capacitor/preferences`,
 * which despite its name is a THIN WRAPPER OVER PLAINTEXT `UserDefaults`
 * (iOS) / plaintext `SharedPreferences` (Android) and must never be used for
 * long-lived credentials. We stamp the token back onto `/api/auth/refresh`
 * via the `X-Refresh-Token` header (see `api/createApiClient.ts`).
 *
 * `accessControl` is left at its default (`AccessControl.NONE`): the token
 * must be readable without a biometric prompt so silent background refresh
 * (the axios interceptor, on every API call) keeps working. This is a secure
 * storage location, not a biometric gate — `biometricAuth.ts` provides the
 * separate app-unlock gate via its own sentinel credential. We reuse that
 * module's per-app keychain scope (see {@link getKeychainServer}) but
 * namespace it with a distinct `server` string so the refresh-token
 * credential and the biometric sentinel never collide or overwrite one
 * another.
 *
 * The helpers are no-ops in web; the backend only inspects the header on
 * `/api/auth/refresh` so an accidental call elsewhere wouldn't matter, but
 * the no-op keeps the contract honest: web does cookies, native does headers.
 */

import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { getKeychainServer } from './biometricAuth';

// The `Credentials` API on NativeBiometric stores one username/password pair
// per `server` string — it's not a general key-value store — so we mint a
// dedicated server string for this credential and use a fixed placeholder
// username; the refresh token itself is the `password` field.
const CREDENTIAL_USERNAME = 'nativeRefreshToken';

function credentialServer(): string {
  return `${getKeychainServer()}.refreshToken`;
}

/** True when running inside the Capacitor native shell. */
function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Persist the refresh token from a login / refresh response. No-op on web —
 * web clients rely on the HttpOnly cookie path. Pass null/undefined to skip
 * silently; safe to call on responses that don't include a token.
 */
export async function storeRefreshTokenIfNative(token: string | null | undefined): Promise<void> {
  if (!isNative()) return;
  if (!token) return;
  try {
    await NativeBiometric.setCredentials({
      username: CREDENTIAL_USERNAME,
      password: token,
      server: credentialServer(),
    });
  } catch (err) {
    // Storage failures shouldn't break login — log and continue. If we can't
    // persist, next launch will fall back to the password sign-in flow.
    console.warn('Failed to persist native refresh token', err);
  }
}

/** Read the stored token, or null when web / not yet stored / read failed. */
export async function getStoredRefreshToken(): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const { password } = await NativeBiometric.getCredentials({ server: credentialServer() });
    return password ?? null;
  } catch {
    // getCredentials rejects when nothing is stored yet for this server —
    // that's the normal "not logged in on this device" state, not a real
    // error, so we stay quiet here (unlike the write/delete paths below).
    return null;
  }
}

/** Drop the stored token. Safe to call when not enrolled / on web. */
export async function clearStoredRefreshToken(): Promise<void> {
  if (!isNative()) return;
  try {
    await NativeBiometric.deleteCredentials({ server: credentialServer() });
  } catch (err) {
    // No credentials to delete is fine — deleteCredentials on some platforms
    // rejects rather than no-ops when the server has nothing stored.
    console.warn('Failed to clear native refresh token', err);
  }
}
